import React, { useEffect, useRef } from 'react';
import { usePrevious, HeartFilledIcon, Stacker, TypographyBody, useHoneycombTheme, Styler } from '@honeycomb/ui-core';
import { createRoot } from 'react-dom/client';
import { LocalStorageEnum, parseBoolean, PriceDisplayMode, useGlobalContext, useShortlist, } from '@honeycomb/data';
const PIN_HEIGHT = 26;
export function MapMarker({ estabId, map, coordinates, title, selected, onClick, onSelected, pricingSummary, }) {
    var _a, _b, _c, _d, _e;
    const { configSettings: { EnableShortlist }, } = useGlobalContext();
    const markerRef = useRef(null);
    const containerRef = useRef(null);
    const rootRef = useRef(null);
    const prevSelected = usePrevious(selected);
    const priceDisplayMode = typeof window !== 'undefined'
        ? (_a = window.localStorage.getItem(LocalStorageEnum.PRICE_DISPLAY_MODE)) !== null && _a !== void 0 ? _a : PriceDisplayMode.TotalPrice
        : PriceDisplayMode.TotalPrice;
    const isPricePerPerson = (priceDisplayMode && priceDisplayMode === PriceDisplayMode.PerPersonPrice) || false;
    const priceToShow = isPricePerPerson
        ? (_b = pricingSummary === null || pricingSummary === void 0 ? void 0 : pricingSummary.teaserPricePerPerson) === null || _b === void 0 ? void 0 : _b.formatted
        : (_c = pricingSummary === null || pricingSummary === void 0 ? void 0 : pricingSummary.teaserPrice) === null || _c === void 0 ? void 0 : _c.formatted;
    const shortlistEnabled = parseBoolean(EnableShortlist);
    const getShortlistResults = useShortlist([], !shortlistEnabled);
    const estabsInShortlist = React.useMemo(() => { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = getShortlistResults.data) === null || _a === void 0 ? void 0 : _a.getShortlist) === null || _b === void 0 ? void 0 : _b.hotels) === null || _c === void 0 ? void 0 : _c.map((hotel) => hotel.estabId.toString())) !== null && _d !== void 0 ? _d : []; }, [(_e = (_d = getShortlistResults.data) === null || _d === void 0 ? void 0 : _d.getShortlist) === null || _e === void 0 ? void 0 : _e.hotels]);
    const { theme: { palette }, } = useHoneycombTheme();
    const textColor = selected ? 'common.white' : undefined;
    const backgroundColor = selected ? palette.primary.base : palette.common.white;
    // Effect: create marker only once on mount
    useEffect(() => {
        if (markerRef.current || !map) {
            return undefined;
        }
        const container = document.createElement('div');
        containerRef.current = container;
        const root = createRoot(container);
        rootRef.current = root;
        const marker = new google.maps.marker.AdvancedMarkerElement({
            position: coordinates,
            title,
            content: container,
        });
        marker.map = map;
        marker.addListener('click', () => onClick === null || onClick === void 0 ? void 0 : onClick(marker));
        markerRef.current = marker;
        // Cleanup marker and React root on unmount or dependency change.
        return () => {
            // Defer root.unmount() so React finish rendering before unmount
            const rootToUnmount = rootRef.current;
            if (rootToUnmount) {
                queueMicrotask(() => {
                    rootToUnmount.unmount();
                });
            }
            if (markerRef.current) {
                markerRef.current.map = null;
            }
            markerRef.current = null;
            rootRef.current = null;
            containerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]); // only recreate marker if `map` changes
    // Effect: update marker props when coordinates or title change
    useEffect(() => {
        const marker = markerRef.current;
        if (!marker)
            return;
        const pos = marker.position;
        const lat = typeof (pos === null || pos === void 0 ? void 0 : pos.lat) === 'function' ? pos.lat() : pos === null || pos === void 0 ? void 0 : pos.lat;
        const lng = typeof (pos === null || pos === void 0 ? void 0 : pos.lng) === 'function' ? pos.lng() : pos === null || pos === void 0 ? void 0 : pos.lng;
        if (lat !== coordinates.lat || lng !== coordinates.lng) {
            marker.position = coordinates;
        }
        if (marker.title !== title) {
            marker.title = title;
        }
    }, [coordinates, title]);
    // Effect: Reattach to map if map reference changes
    useEffect(() => {
        const marker = markerRef.current;
        if (marker && map) {
            marker.map = map;
        }
    }, [map]);
    // Effect: Only update content visually without resetting marker
    useEffect(() => {
        const root = rootRef.current;
        const marker = markerRef.current;
        if (!root || !marker) {
            return;
        }
        root.render(React.createElement(Styler, { h: PIN_HEIGHT, br: PIN_HEIGHT / 2, px: 2, bg: backgroundColor, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "m", "data-id": "map_marker_content" }, priceToShow ? (React.createElement(Stacker, { direction: "row", alignItems: "center", gap: 1 },
            React.createElement(TypographyBody, { weight: "bold", size: "xs", color: textColor }, priceToShow),
            (estabsInShortlist === null || estabsInShortlist === void 0 ? void 0 : estabsInShortlist.includes(estabId)) && React.createElement(HeartFilledIcon, { size: "xs", color: textColor }))) : (
        // For landing pages where we don't have pricing summary
        React.createElement(TypographyBody, { weight: "bold", size: "xs", color: textColor }, title))));
        if (selected && selected !== prevSelected) {
            onSelected === null || onSelected === void 0 ? void 0 : onSelected(marker);
        }
    }, [
        priceToShow,
        selected,
        prevSelected,
        onSelected,
        estabsInShortlist,
        estabId,
        backgroundColor,
        textColor,
        title,
    ]);
    return null;
}
